import type {
  SessionRecordingSummary,
  TelemetryNote,
  VideoFile,
} from "@/types";
import type { VideoSessionTimeline } from "@/utils/videoSessionTimeline";
import type { VideoControls } from "@/hooks/useAnalyticsKeyboardShortcuts";
import SynchronizedVideoPlayer from "./SynchronizedVideoPlayer";
import VideoOnlyTimeline from "./VideoOnlyTimeline";

interface VideoOnlyAnalyticsLayoutProps {
  videos: VideoFile[];
  recordings?: SessionRecordingSummary[];
  currentTime: number;
  onVideoTimeUpdate: (time: number) => void;
  onVideoSelect: (videoId: string) => void;
  initialVideoId?: string;
  requestedVideoId?: string;
  onDurationChange: (videoId: string, duration: number) => void;
  onGetVideoControls: (controls: VideoControls) => void;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onRefreshRecordingPlayback?: (recordingId: string) => Promise<string | null>;
  anchorTimestamp: string | null;
  isUsingSessionFallback: boolean;
  durationSeconds: number | null;
  timeline: VideoSessionTimeline | null;
  notes: TelemetryNote[];
  onNoteClick: (note: TelemetryNote) => void;
  onNoteCreate: (timeOffset: number) => void;
  notesEnabled: boolean;
  onTimelineTimeChange: (time: number) => void;
}

/**
 * "Center stage" layout for sessions with videos but no telemetry:
 * dominant centered video with a full-width docked timeline. Rendered by
 * SessionAnalyticsPage instead of the 3-pane PanelGroup.
 */
export default function VideoOnlyAnalyticsLayout({
  videos,
  recordings,
  currentTime,
  onVideoTimeUpdate,
  onVideoSelect,
  initialVideoId,
  requestedVideoId,
  onDurationChange,
  onGetVideoControls,
  volume,
  onVolumeChange,
  onRefreshRecordingPlayback,
  anchorTimestamp,
  isUsingSessionFallback,
  durationSeconds,
  timeline,
  notes,
  onNoteClick,
  onNoteCreate,
  notesEnabled,
  onTimelineTimeChange,
}: VideoOnlyAnalyticsLayoutProps) {
  return (
    <div className="flex h-full flex-col bg-[#0d0d14]">
      <div className="flex min-h-0 flex-1 items-center justify-center p-4">
        <SynchronizedVideoPlayer
          videos={videos}
          recordings={recordings}
          currentTime={currentTime}
          onTimeUpdate={onVideoTimeUpdate}
          onVideoSelect={onVideoSelect}
          initialVideoId={initialVideoId}
          requestedVideoId={requestedVideoId}
          onDurationChange={onDurationChange}
          onGetVideoControls={onGetVideoControls}
          volume={volume}
          onVolumeChange={onVolumeChange}
          timeline={timeline}
          onRefreshRecordingPlayback={onRefreshRecordingPlayback}
          fillAvailableSpace
          className="h-full w-full"
        />
      </div>
      <div className="flex-shrink-0 border-t border-[#1a1a2e]">
        <VideoOnlyTimeline
          variant="docked"
          anchorTimestamp={anchorTimestamp}
          durationSeconds={durationSeconds}
          currentTime={currentTime}
          onTimeChange={onTimelineTimeChange}
          isUsingSessionFallback={isUsingSessionFallback}
          timeline={timeline}
          notes={notes}
          onNoteClick={onNoteClick}
          onNoteCreate={onNoteCreate}
          notesEnabled={notesEnabled}
        />
      </div>
    </div>
  );
}
